import { Box, Divider, Flex, Heading, HStack, Text } from "@chakra-ui/layout";
import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type BillingPlanBudgetInvoice,
  BillingPlanBudgetInvoiceTypeEnum,
  BillingPlanBudgetItemInvoiceTypeEnum,
  ProjectBillingPlanSettingGenerateInvoiceItemsEnum,
} from "@src/__generated__/graphql";
import {
  formatDate,
  type IOption,
  Link,
  Select,
  TextField,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { toApiDate } from "@src/utils/dates";
import { currency, denominate } from "@src/utils/formatters";
import type { DateRange } from "@src/utils/types";
import { BankAccountSelect } from "@src/widgets/BankAccountSelect";
import { CompanySelect } from "@src/widgets/CompanySelect";
import { UserSelect } from "@src/widgets/UserSelect";
import type { FieldState } from "formstate";
import { observer } from "mobx-react-lite";
import type { IssueInvoicesForm } from "./models/issue-invoice-form";
import type { ProjectBillingPlanRow } from "./models/row";
import type { ProjectBillingPlanSubRow } from "./models/subrow";

function getCellColor(planned: number, budget: number): string | undefined {
  return planned < budget
    ? "red.500"
    : planned > budget
      ? "orange.400"
      : undefined;
}

function getTotalBudget(rows: ProjectBillingPlanRow[]): number {
  return rows.reduce((total, row) => total + row.total_budget, 0);
}

function getTotalPlanned(rows: ProjectBillingPlanRow[]): number {
  return rows.reduce((total, row) => total + (row.planned ?? 0), 0);
}

function getTotalDifference(rows: ProjectBillingPlanRow[]): number {
  return rows.reduce((total, row) => total + (row.difference ?? 0), 0);
}

function getTotalPlannedForMonth(
  rows: ProjectBillingPlanRow[],
  month: Date,
  denomination: number,
): number {
  return rows.reduce((rowTotal, { __subRows }) => {
    return (
      rowTotal +
      __subRows.reduce((subRowTotal, { $ }) => {
        return (
          subRowTotal +
          denominate(
            Number($.months.$.get(toApiDate(month))?.$ ?? "0"),
            denomination,
          )
        );
      }, 0)
    );
  }, 0);
}

type BudgetItemProps = {
  row: ProjectBillingPlanRow;
  showTitle: boolean;
};
export const BudgetItem = observer(function BudgetItem({
  row,
  showTitle,
}: BudgetItemProps) {
  return (
    <Text>
      {showTitle && <Text fontWeight="bold">{row.title}</Text>}
      {row.category && showTitle ? (
        <Text>({row.category.name})</Text>
      ) : (
        <Text fontWeight="bold">{row.category?.name}</Text>
      )}
    </Text>
  );
});

type TypeProps = {
  field: FieldState<BillingPlanBudgetItemInvoiceTypeEnum | undefined>;
  onChange: (value: BillingPlanBudgetItemInvoiceTypeEnum | undefined) => void;
};
export const Type = observer(function Type({
  field: type,
  onChange,
}: TypeProps) {
  return (
    <Box>
      <Select
        size="sm"
        options={[
          {
            value: BillingPlanBudgetItemInvoiceTypeEnum.OutgoingInvoice,
            label: t`Invoice`,
          },
          {
            value: BillingPlanBudgetItemInvoiceTypeEnum.OutgoingProformaInvoice,
            label: t`Proforma`,
          },
        ]}
        value={type.value}
        onChange={onChange}
        error={type.error}
        isClearable
        asPortal
        components={{
          ValueContainer: (props) => (
            <Flex pl="1.5" align="center" mr="auto">
              {props.children}
            </Flex>
          ),
          SingleValue: (props) => <Text fontSize="xs">{props.children}</Text>,
        }}
      />
    </Box>
  );
});

type DayProps = {
  field: ProjectBillingPlanSubRow["$"]["day"];
  dayOptions: IOption[];
  onChange: (value: string) => void;
};
export const Day = observer(function Day({
  field,
  dayOptions,
  onChange,
}: DayProps) {
  return field ? (
    <Box minW="110px">
      <Select
        asPortal
        isClearable
        options={dayOptions}
        value={field.value?.toString()}
        onChange={onChange}
        error={field.error}
        placeholder={t`Day`}
      />
    </Box>
  ) : null;
});

type ActionsProps = {
  row: ProjectBillingPlanRow;
  subRow: ProjectBillingPlanSubRow;
  period: DateRange;
};
export const Actions = observer(function Actions({
  row,
  subRow,
  period,
}: ActionsProps) {
  return (
    <HStack>
      <Menu>
        <Tooltip label={t`Distribute whole budget`} placement="top">
          <MenuButton
            as={IconButton}
            aria-label={t`Distribute whole budget`}
            colorScheme="gray"
            icon={<Icon name="distribute" h="4" />}
            variant="ghost"
          />
        </Tooltip>
        <MenuList>
          <Text pl="3" fontWeight="bold" pb="1">
            <Trans>Distribute whole budget:</Trans>
          </Text>
          <MenuItem
            onClick={() => {
              subRow.distributeBetweenMonths(row.total_budget, 1);
            }}
          >
            <Trans>Monthly</Trans>
          </MenuItem>
          <MenuItem
            isDisabled={subRow.monthCount < 2}
            onClick={() => {
              subRow.distributeBetweenMonths(row.total_budget, 2);
            }}
          >
            <Trans>Bi-monthly</Trans>
          </MenuItem>
          <MenuItem
            isDisabled={subRow.monthCount < 3}
            onClick={() => {
              subRow.distributeBetweenMonths(row.total_budget, 3);
            }}
          >
            <Trans>Quarterly</Trans>
          </MenuItem>
          <MenuItem
            isDisabled={subRow.monthCount < 6}
            onClick={() => {
              subRow.distributeBetweenMonths(row.total_budget, 6);
            }}
          >
            <Trans>Half-yearly</Trans>
          </MenuItem>
          <MenuItem
            onClick={() => {
              subRow.duplicateFirstMonthToRest(period);
            }}
          >
            <Trans>Duplicate first month to all months</Trans>
          </MenuItem>
        </MenuList>
      </Menu>
      <Tooltip label={t`Add new row`} placement="top">
        <IconButton
          aria-label={t`Add new row`}
          colorScheme="gray"
          icon={<Icon name="plus" />}
          onClick={() => {
            row.addSubRow(subRow.id, period);
          }}
          variant="ghost"
        />
      </Tooltip>
      <Tooltip
        label={
          row.isRemoveButtonDisabled
            ? t`Can't remove budget item`
            : t`Remove row`
        }
        placement="top"
      >
        <IconButton
          aria-label={t`Remove row`}
          colorScheme="gray"
          icon={<Icon name="trash-03" />}
          isDisabled={row.isRemoveButtonDisabled}
          onClick={() => {
            row.removeRow(subRow.id, period);
          }}
          variant="ghost"
        />
      </Tooltip>
    </HStack>
  );
});

type MonthInputProps = {
  field: FieldState<string> | undefined;
  currencyCode?: string;
};
export const MonthInput = observer(function MonthInput({
  field,
  currencyCode,
}: MonthInputProps) {
  if (!field) return null;
  return (
    <FormControl isInvalid={field.hasError}>
      <InputGroup justifyContent="end">
        <Input
          px="3"
          textAlign="end"
          errorBorderColor="red.500"
          onBlur={({ target }) => {
            if (target.value !== "") return;
            field.onChange("0");
          }}
          onChange={({ target }) => {
            field.onChange(target.value);
          }}
          onFocus={({ target }) => {
            if (target.value !== "0") return;
            field.onChange("");
          }}
          onWheel={({ currentTarget }) => {
            currentTarget.blur();
          }}
          type="number"
          value={field.value}
        />
        <InputRightAddon px="2">
          <Text>&nbsp;{currencyCode}</Text>
        </InputRightAddon>
      </InputGroup>
    </FormControl>
  );
});

type BudgetFooterProps = {
  rows: ProjectBillingPlanRow[];
};
export const BudgetFooter = observer(function BudgetFooter({
  rows,
}: BudgetFooterProps) {
  return (
    <Text fontWeight="bold" fontSize="sm">
      {currency.formatByWorkspace(getTotalBudget(rows))}
    </Text>
  );
});

type PlannedCellProps = {
  row: ProjectBillingPlanRow;
};
export const PlannedCell = observer(function PlannedCell({
  row,
}: PlannedCellProps) {
  if (typeof row.planned !== "number") return null;

  return (
    <Text color={getCellColor(row.planned, row.total_budget)} fontSize="sm">
      {currency.formatByWorkspace(row.planned)}
    </Text>
  );
});

type PlannedFooterProps = {
  rows: ProjectBillingPlanRow[];
};
export const PlannedFooter = observer(function PlannedFooter({
  rows,
}: PlannedFooterProps) {
  const totalBudget = getTotalBudget(rows);
  const totalPlanned = getTotalPlanned(rows);

  return (
    <Text
      fontWeight="bold"
      fontSize="sm"
      color={getCellColor(totalPlanned, totalBudget)}
    >
      {currency.formatByWorkspace(totalPlanned)}
    </Text>
  );
});

type DifferenceCellProps = {
  row: ProjectBillingPlanRow;
};
export const DifferenceCell = observer(function DifferenceCell({
  row,
}: DifferenceCellProps) {
  if (typeof row.difference !== "number") return null;

  return (
    <Text color={getCellColor(row.difference, 0)} fontSize="sm" mr="2">
      {currency.formatByWorkspace(row.difference)}
    </Text>
  );
});

type DifferenceFooterProps = {
  rows: ProjectBillingPlanRow[];
};
export const DifferenceFooter = observer(function DifferenceFooter({
  rows,
}: DifferenceFooterProps) {
  const totalDifference = getTotalDifference(rows);

  return (
    <Text
      color={getCellColor(totalDifference, 0)}
      fontWeight="bold"
      fontSize="sm"
      mr="2"
    >
      {currency.formatByWorkspace(totalDifference)}
    </Text>
  );
});

type MonthFooterProps = {
  rows: ProjectBillingPlanRow[];
  month: Date;
  denomination: number;
};
export const MonthFooter = observer(function MonthFooter({
  rows,
  month,
  denomination,
}: MonthFooterProps) {
  return (
    <Text fontSize="sm">
      {currency.formatByWorkspace(
        getTotalPlannedForMonth(rows, month, denomination),
      )}
    </Text>
  );
});

function getInvoicePathname(type: BillingPlanBudgetInvoiceTypeEnum) {
  switch (type) {
    case BillingPlanBudgetInvoiceTypeEnum.OutgoingInvoice:
      return "/invoices/outgoing-invoices/detail";
    case BillingPlanBudgetInvoiceTypeEnum.OutgoingProformaInvoice:
      return "/invoices/proforma-invoices/detail";
    default:
      return "/invoices/for-invoicing/edit";
  }
}

type InvoicesPopoverProps = {
  invoices: BillingPlanBudgetInvoice[];
};
export const InvoicesPopover = observer(function InvoicesPopover({
  invoices,
}: InvoicesPopoverProps) {
  if (!invoices.length) return null;
  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <Box>
          <Icon name="info-circle" />
        </Box>
      </PopoverTrigger>
      <PopoverContent p="2">
        <PopoverBody overflowY="auto" maxH="300px">
          <Heading size="md" textAlign="start" mb="2">
            <Trans>Invoices</Trans>
          </Heading>
          <VStack align="start">
            {invoices.map((invoice) => (
              <Link
                key={`${invoice.invoice_id}-${invoice.date}-${invoice.invoice_type}`}
                target="_blank"
                href={{
                  pathname: getInvoicePathname(invoice.invoice_type),
                  query: {
                    id: invoice.invoice_id,
                  },
                }}
              >
                {formatDate(new Date(invoice.date))}&nbsp;-&nbsp;
                {currency.formatByWorkspace(invoice.amount)}
              </Link>
            ))}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
});

export type IssueInvoicesModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  form: IssueInvoicesForm | undefined;
  vatRateOptions: IOption[];
  isDisabled: boolean;
  onInvoiceItemsChange: (
    value: ProjectBillingPlanSettingGenerateInvoiceItemsEnum | undefined,
  ) => void;
  onBillingEntityChange?: (value: string | undefined) => void;
  onContactChange?: (value: string | undefined) => void;
};

export const IssueInvoicesModal = observer(function IssueInvoicesModal({
  isOpen,
  onClose,
  form,
  vatRateOptions,
  isDisabled,
  onInvoiceItemsChange,
  onBillingEntityChange,
  onContactChange,
}: IssueInvoicesModalProps) {
  if (!form) return null;

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={() => onClose?.()}
      scrollBehavior="inside"
      size="3xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Issue invoices automatically</Trans>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody>
          <HStack justifyContent="space-between">
            <Box>
              <FormLabel fontWeight="bold">
                <Trans>Invoice items:</Trans>
              </FormLabel>

              <FormControl isInvalid={form.$.generateInvoiceItems.hasError}>
                <RadioGroup
                  isDisabled={isDisabled}
                  onChange={(value) => {
                    onInvoiceItemsChange(
                      value as
                        | ProjectBillingPlanSettingGenerateInvoiceItemsEnum
                        | undefined,
                    );
                  }}
                  value={form.$.generateInvoiceItems.value ?? undefined}
                >
                  <VStack align="start" w="full" spacing="2">
                    <Radio
                      value={
                        ProjectBillingPlanSettingGenerateInvoiceItemsEnum.ProjectName
                      }
                    >
                      <Trans>Project name (single item)</Trans>
                    </Radio>
                    <Radio
                      value={
                        ProjectBillingPlanSettingGenerateInvoiceItemsEnum.CategoryName
                      }
                    >
                      <Trans>
                        Work & Expenses (media, production) as separate items
                      </Trans>
                    </Radio>
                    <Radio
                      value={
                        ProjectBillingPlanSettingGenerateInvoiceItemsEnum.BudgetItemName
                      }
                    >
                      <Trans>Every budget item as separate item</Trans>
                    </Radio>
                    <Radio display="none" value={undefined} />
                  </VStack>
                </RadioGroup>
                <FormErrorMessage>
                  {form.$.generateInvoiceItems.error}
                </FormErrorMessage>
              </FormControl>
            </Box>

            <Box>
              <Box mb="4">
                <FormLabel fontWeight="bold">
                  <Trans>Vat rate</Trans>
                </FormLabel>
                <Select
                  isDisabled={isDisabled}
                  placeholder={t`Vat rate`}
                  options={vatRateOptions}
                  onChange={form.$.vatRateId.onChange}
                  error={form.$.vatRateId.error}
                  value={form.$.vatRateId.value}
                />
              </Box>
              <FormLabel fontWeight="bold">
                <Trans>Date of supply:</Trans>
              </FormLabel>
              <HStack spacing="2">
                <Box w="80px">
                  <TextField
                    isDisabled={isDisabled}
                    error={form.$.dateOfSupplyFromIssueDate.error}
                    value={form.$.dateOfSupplyFromIssueDate.value.toString()}
                    onChange={(value) => {
                      form.$.dateOfSupplyFromIssueDate.onChange(value);
                    }}
                  />
                </Box>
                <Text w="full">
                  <Trans>days before Issue date</Trans>
                </Text>
              </HStack>
            </Box>
          </HStack>

          <HStack my="4" spacing="4">
            <Heading size="sm" minW="fit-content">
              <Trans>Invoice options</Trans>
            </Heading>
            <Divider />
          </HStack>

          <HStack spacing="2" alignItems="flex-start">
            <VStack align="start" w="full" isTruncated>
              <FormLabel fontWeight="bold">
                <Trans>Legal entity</Trans>
              </FormLabel>
              <CompanySelect
                asPortal
                field={form.$.workspace_company_id}
                onChange={() => {
                  form.$.workspace_bank_account_id.onChange(undefined);
                }}
                isDisabled={isDisabled}
              />
            </VStack>
            <VStack align="start" w="full" isTruncated>
              <FormLabel fontWeight="bold">
                <Trans>Agency bank account</Trans>
              </FormLabel>
              <BankAccountSelect
                asPortal
                isDisabled={isDisabled}
                field={form.$.workspace_bank_account_id}
                companyId={form.$.workspace_company_id.value}
              />
            </VStack>
            {/* <VStack align="start" w="full" isTruncated>
              <FormLabel fontWeight="bold">
                <Trans>Invoice sequence number</Trans>
              </FormLabel>
              <TextField
                isDisabled={isDisabled}
                value={form.$.invoice_sequence_no.value}
                placeholder={t`Invoice sequence number`}
                onChange={form.$.invoice_sequence_no.onChange}
              />
            </VStack> */}
          </HStack>

          <HStack mt="4" spacing="2" alignItems="flex-start">
            <VStack align="start" w="full" maxW="33%">
              <FormLabel fontWeight="bold">
                <Trans>Billing address</Trans>
              </FormLabel>
              <Select
                asPortal
                options={form.billingEntitiesOptions}
                value={form.$.billing_entity_id.value}
                isDisabled={isDisabled}
                isClearable
                onChange={
                  onBillingEntityChange ?? form.handleBillingEntityChange
                }
                error={form.$.billing_entity_id.error}
              />
            </VStack>

            <VStack align="start" w="full" maxW="33%">
              <FormLabel fontWeight="bold">
                <Trans>Contact person</Trans>
              </FormLabel>
              <UserSelect
                asPortal
                isClearable
                options={form.contactsOptions}
                value={form.$.contact_id.value}
                onChange={onContactChange ?? form.$.contact_id.onChange}
                error={form.$.contact_id.error}
                isLoading={form.isLoadingOptions}
                isDisabled={isDisabled}
              />
            </VStack>

            <VStack align="start" w="full" maxW="33%">
              <FormLabel fontWeight="bold">
                <Trans>Purchase order number</Trans>
              </FormLabel>
              <Select
                asPortal
                isClearable
                isDisabled={isDisabled}
                value={form.$.order_id.value}
                placeholder={t`Purchase order number`}
                onChange={form.$.order_id.onChange}
                options={form.ordersOptions}
                isLoading={form.isLoadingOptions}
              />
            </VStack>
          </HStack>

          <VStack align="start" w="full" my="2" spacing="2">
            <HStack spacing="2" w="full">
              <FormControl flex="1" display="inline-flex">
                <Checkbox
                  isChecked={
                    form.$.create_draft_invoice.value ||
                    form.hasCurrencyMismatch
                  }
                  isDisabled={isDisabled || form.hasCurrencyMismatch}
                  onChange={({ target }) =>
                    form.$.create_draft_invoice.onChange(target.checked)
                  }
                >
                  <Trans>Create as draft</Trans>
                </Checkbox>
              </FormControl>

              <Flex flex="1">
                <Checkbox
                  isChecked={form.$.create_summary_invoice_after_payment.value}
                  isDisabled={isDisabled}
                  onChange={({ target }) =>
                    form.$.create_summary_invoice_after_payment.onChange(
                      target.checked,
                    )
                  }
                >
                  <Trans>Create summary invoice after proforma payment</Trans>
                </Checkbox>
              </Flex>
            </HStack>
          </VStack>

          <FormLabel fontWeight="bold">
            <Trans>Text on invoice</Trans>
          </FormLabel>
          <Textarea
            isDisabled={isDisabled}
            onChange={({ target }) => {
              form.$.text_on_invoice.onChange(target.value);
            }}
            placeholder={t`Text on invoice`}
            value={form.$.text_on_invoice.value}
          />

          <HStack mt="4" spacing="2" alignItems="flex-start">
            <Box w="full">
              <FormLabel fontWeight="bold">
                <Trans>Note on invoice</Trans>
              </FormLabel>
              <Textarea
                isDisabled={isDisabled}
                onChange={({ target }) => {
                  form.$.note.onChange(target.value);
                }}
                placeholder={t`Note on invoice`}
                value={form.$.note.value}
              />
            </Box>
            <Box w="full">
              <FormLabel display="inline-flex">
                <Text fontWeight="bold">
                  <Trans>Internal note</Trans>
                </Text>
                &nbsp;
                <Text>
                  <Trans>(Not visible on invoice)</Trans>
                </Text>
              </FormLabel>
              <Textarea
                isDisabled={isDisabled}
                onChange={({ target }) => {
                  form.$.internal_note.onChange(target.value);
                }}
                placeholder={t`Internal note`}
                value={form.$.internal_note.value}
              />
            </Box>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" onClick={onClose} variant="subtle">
            <Trans>Close</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
